import React, { useState, useEffect, useCallback} from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../../../context/AuthContext';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import {
  Activity,
  Plus,
  Search,
  RefreshCw,
  PlayCircle,
  Clock,
  Calendar,
  User,
  Eye,
  Pencil
} from 'lucide-react';

const ACTIVITY_TYPES = [
  { value: 'INTERNAL', label: 'Internal (Labor)' },
  { value: 'EXTERNAL', label: 'External (Subcontracting)' },
  { value: 'SERVICE', label: 'Service' },
  { value: 'COST', label: 'Cost' },
  { value: 'GENERAL_COST', label: 'General Cost' }
];

const CONTROL_KEYS = [
  { value: 'PS01', label: 'PS01 - Internal Processing' },
  { value: 'PS02', label: 'PS02 - External Processing' },
  { value: 'PS03', label: 'PS03 - Cost Activity' },
  { value: 'PS04', label: 'PS04 - Service Activity' },
  { value: 'PS05', label: 'PS05 - Milestone' },
  { value: 'PS06', label: 'PS06 - General Costs' }
];

export default function Activities() {
  const [searchParams] = useSearchParams();
  const { canWrite } = useAuth();
  const initialNetworkId = searchParams.get('network_id') || '';
  
  const [activities, setActivities] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewEditDialog, setShowViewEditDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterNetwork, setFilterNetwork] = useState(initialNetworkId || '__all__');
  const [filterStatus, setFilterStatus] = useState('__all__');
  const [filterType, setFilterType] = useState('__all__');

  const [formData, setFormData] = useState({
    network_id: initialNetworkId,
    activity_number: '',
    description: '',
    activity_type: 'INTERNAL',
    control_key: 'PS01',
    work_center_id: '',
    planned_work: 0,
    planned_duration: 1,
    earliest_start_date: '',
    earliest_finish_date: ''
  });

  const [editFormData, setEditFormData] = useState({
    description: '',
    planned_work: 0,
    planned_duration: 1,
    earliest_start_date: '',
    earliest_finish_date: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterNetwork && filterNetwork !== '__all__') params.network_id = filterNetwork;
      if (filterStatus && filterStatus !== '__all__') params.activity_status = filterStatus;
      if (filterType && filterType !== '__all__') params.activity_type = filterType;
      
      const [activitiesRes, networksRes, projectsRes] = await Promise.all([
        psAPI.getActivities(params),
        psAPI.getNetworks(),
        psAPI.getProjects()
      ]);
      setActivities(activitiesRes.data);
      setNetworks(networksRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load activities');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filterNetwork, filterStatus, filterType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const handleCreate = async () => {
    try {
      const payload = {
        ...formData,
        planned_work: parseFloat(formData.planned_work) || 0,
        planned_duration: parseInt(formData.planned_duration) || 1
      };
      if (!payload.work_center_id) delete payload.work_center_id;
      if (!payload.activity_number) delete payload.activity_number;
      // Remove empty date strings
      if (!payload.earliest_start_date) delete payload.earliest_start_date;
      if (!payload.earliest_finish_date) delete payload.earliest_finish_date;
      
      await psAPI.createActivity(payload);
      toast.success('Activity created successfully');
      setShowCreateDialog(false);
      setFormData({
        network_id: filterNetwork !== '__all__' ? filterNetwork : '',
        activity_number: '',
        description: '',
        activity_type: 'INTERNAL',
        control_key: 'PS01',
        work_center_id: '',
        planned_work: 0,
        planned_duration: 1,
        earliest_start_date: '',
        earliest_finish_date: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create activity');
    }
  };

  const handleRelease = async (activityId) => {
    try {
      await psAPI.releaseActivity(activityId);
      toast.success('Activity released successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release activity');
    }
  };

  const handleView = async (activity) => {
    try {
      const response = await psAPI.getActivity(activity.activity_id);
      setSelectedActivity(response.data);
      setEditFormData({
        description: response.data.description || '',
        planned_work: response.data.planned_work || 0,
        planned_duration: response.data.planned_duration || 1,
        earliest_start_date: response.data.earliest_start_date || '',
        earliest_finish_date: response.data.earliest_finish_date || ''
      });
      setIsEditMode(false);
      setShowViewEditDialog(true);
    } catch (error) {
      toast.error('Failed to load activity details');
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleSaveEdit = async () => {
    try {
      const payload = {
        description: editFormData.description || undefined,
        planned_work: parseFloat(editFormData.planned_work) || undefined,
        planned_duration: parseInt(editFormData.planned_duration) || undefined
      };
      // Only include dates if they have values
      if (editFormData.earliest_start_date) {
        payload.earliest_start_date = editFormData.earliest_start_date;
      }
      if (editFormData.earliest_finish_date) {
        payload.earliest_finish_date = editFormData.earliest_finish_date;
      }
      
      await psAPI.updateActivity(selectedActivity.activity_id, payload);
      toast.success('Activity updated successfully');
      setShowViewEditDialog(false);
      setSelectedActivity(null);
      setIsEditMode(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update activity');
    }
  };

  const filteredActivities = activities.filter(a => {
    const matchesSearch = !searchQuery || 
      a.activity_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const getStatusBadge = (status) => {
    const variants = {
      'CREATED': 'bg-gray-100 text-gray-700',
      'RELEASED': 'bg-orange-100 text-orange-700',
      'PARTIALLY_CONFIRMED': 'bg-amber-100 text-amber-700',
      'CONFIRMED': 'bg-emerald-100 text-emerald-700',
      'TECO': 'bg-orange-100 text-orange-700',
      'CLOSED': 'bg-slate-100 text-slate-700'
    };
    return <Badge className={variants[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const getTypeBadge = (type) => {
    const variants = {
      'INTERNAL': 'bg-orange-100 text-orange-700',
      'EXTERNAL': 'bg-orange-100 text-orange-700',
      'SERVICE': 'bg-green-100 text-green-700',
      'COST': 'bg-orange-100 text-orange-700',
      'GENERAL_COST': 'bg-gray-100 text-gray-700'
    };
    return <Badge className={variants[type] || 'bg-gray-100'}>{type}</Badge>;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Activities</h1>
            <p className="text-sm text-gray-500 mt-1">Manage network activities</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {canWrite('project_system') && (
            <Button onClick={() => setShowCreateDialog(true)} data-testid="create-activity-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Activity
            </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search activities..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-activities"
                  />
                </div>
              </div>
              <div className="w-48">
                <Select value={filterNetwork} onValueChange={setFilterNetwork}>
                  <SelectTrigger data-testid="filter-network">
                    <SelectValue placeholder="All Networks" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Networks</SelectItem>
                    {networks.map(n => (
                      <SelectItem key={n.network_id} value={n.network_id}>{n.network_id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger data-testid="filter-status">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Status</SelectItem>
                    <SelectItem value="CREATED">Created</SelectItem>
                    <SelectItem value="RELEASED">Released</SelectItem>
                    <SelectItem value="PARTIALLY_CONFIRMED">Partially Confirmed</SelectItem>
                    <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger data-testid="filter-type">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Types</SelectItem>
                    {ACTIVITY_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Activities List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Activities ({filteredActivities.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="w-6 h-6 animate-spin text-orange-600" />
              </div>
            ) : filteredActivities.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No activities found</p>
                {canWrite('project_system') && (
                <Button onClick={() => setShowCreateDialog(true)} variant="link" className="mt-2">
                  Create your first activity
                </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3">Activity ID</th>
                      <th className="text-left p-3">Network</th>
                      <th className="text-left p-3">Description</th>
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Planned Work</th>
                      <th className="text-right p-3">Actual Work</th>
                      <th className="text-left p-3">Progress</th>
                      <th className="text-left p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredActivities.map((activity) => (
                      <tr key={activity.activity_id} className="border-b hover:bg-gray-50" data-testid={`activity-row-${activity.activity_id}`}>
                        <td className="p-3 font-medium text-orange-600">{activity.activity_id}</td>
                        <td className="p-3">{activity.network_id}</td>
                        <td className="p-3">{activity.description || '-'}</td>
                        <td className="p-3">{getTypeBadge(activity.activity_type)}</td>
                        <td className="p-3">{getStatusBadge(activity.activity_status)}</td>
                        <td className="p-3 text-right">{activity.planned_work || 0}h</td>
                        <td className="p-3 text-right">{activity.actual_work || 0}h</td>
                        <td className="p-3">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-emerald-600 h-2 rounded-full" 
                              style={{ width: `${activity.progress_percent || 0}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{activity.progress_percent || 0}%</span>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleView(activity)}
                              data-testid={`view-${activity.activity_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {activity.activity_status === 'CREATED' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleRelease(activity.activity_id)}
                                data-testid={`release-${activity.activity_id}`}
                              >
                                <PlayCircle className="w-3 h-3 mr-1" />
                                Release
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Activity</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Network *</Label>
                <Select value={formData.network_id} onValueChange={(v) => setFormData({ ...formData, network_id: v })}>
                  <SelectTrigger data-testid="select-network">
                    <SelectValue placeholder="Select network" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select network</SelectItem>
                    {networks.map(n => (
                      <SelectItem key={n.network_id} value={n.network_id}>{n.network_id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Activity Number (auto if empty)</Label>
                <Input
                  value={formData.activity_number}
                  onChange={(e) => setFormData({ ...formData, activity_number: e.target.value })}
                  placeholder="e.g., 0010"
                  data-testid="input-activity-number"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Activity description"
                data-testid="input-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Activity Type</Label>
                <Select value={formData.activity_type} onValueChange={(v) => setFormData({ ...formData, activity_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Control Key</Label>
                <Select value={formData.control_key} onValueChange={(v) => setFormData({ ...formData, control_key: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTROL_KEYS.map(k => (
                      <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Planned Work (hours)</Label>
                <Input
                  type="number"
                  value={formData.planned_work}
                  onChange={(e) => setFormData({ ...formData, planned_work: e.target.value })}
                  min="0"
                  data-testid="input-planned-work"
                />
              </div>
              <div className="space-y-2">
                <Label>Planned Duration (days)</Label>
                <Input
                  type="number"
                  value={formData.planned_duration}
                  onChange={(e) => setFormData({ ...formData, planned_duration: e.target.value })}
                  min="1"
                  data-testid="input-planned-duration"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.earliest_start_date}
                  onChange={(e) => setFormData({ ...formData, earliest_start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Finish Date</Label>
                <Input
                  type="date"
                  value={formData.earliest_finish_date}
                  onChange={(e) => setFormData({ ...formData, earliest_finish_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreate}
              disabled={!formData.network_id || formData.network_id === '__none__' || !formData.description}
              data-testid="submit-create-activity"
            >
              Create Activity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View/Edit Dialog */}
      <Dialog open={showViewEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowViewEditDialog(false);
          setSelectedActivity(null);
          setIsEditMode(false);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{isEditMode ? 'Edit Activity' : 'Activity Details'}</span>
              {!isEditMode && selectedActivity?.activity_status === 'CREATED' && (
                <Button size="sm" variant="outline" onClick={handleEdit} data-testid="edit-activity-btn">
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedActivity && (
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs text-gray-500">Activity ID</Label>
                  <div className="font-medium">{selectedActivity.activity_id}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Status</Label>
                  <div>{getStatusBadge(selectedActivity.activity_status)}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Network</Label>
                  <div className="font-medium">{selectedActivity.network_id}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Type</Label>
                  <div>{getTypeBadge(selectedActivity.activity_type)}</div>
                </div>
              </div>

              {/* Editable Fields */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Description</Label>
                  {isEditMode ? (
                    <Input
                      value={editFormData.description}
                      onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                      data-testid="edit-description"
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{selectedActivity.description || '-'}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Planned Work (hours)</Label>
                    {isEditMode ? (
                      <Input
                        type="number"
                        value={editFormData.planned_work}
                        onChange={(e) => setEditFormData({ ...editFormData, planned_work: e.target.value })}
                        min="0"
                        data-testid="edit-planned-work"
                      />
                    ) : (
                      <p className="p-2 bg-gray-50 rounded">{selectedActivity.planned_work || 0} hours</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Planned Duration (days)</Label>
                    {isEditMode ? (
                      <Input
                        type="number"
                        value={editFormData.planned_duration}
                        onChange={(e) => setEditFormData({ ...editFormData, planned_duration: e.target.value })}
                        min="1"
                        data-testid="edit-planned-duration"
                      />
                    ) : (
                      <p className="p-2 bg-gray-50 rounded">{selectedActivity.planned_duration || 1} days</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Earliest Start Date</Label>
                    {isEditMode ? (
                      <Input
                        type="date"
                        value={editFormData.earliest_start_date}
                        onChange={(e) => setEditFormData({ ...editFormData, earliest_start_date: e.target.value })}
                        data-testid="edit-start-date"
                      />
                    ) : (
                      <p className="p-2 bg-gray-50 rounded">{selectedActivity.earliest_start_date || '-'}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Earliest Finish Date</Label>
                    {isEditMode ? (
                      <Input
                        type="date"
                        value={editFormData.earliest_finish_date}
                        onChange={(e) => setEditFormData({ ...editFormData, earliest_finish_date: e.target.value })}
                        data-testid="edit-finish-date"
                      />
                    ) : (
                      <p className="p-2 bg-gray-50 rounded">{selectedActivity.earliest_finish_date || '-'}</p>
                    )}
                  </div>
                </div>

                {/* Read-only info */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Actual Work</Label>
                    <p className="font-medium">{selectedActivity.actual_work || 0} hours</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Progress</Label>
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-emerald-600 h-2 rounded-full" 
                          style={{ width: `${selectedActivity.progress_percent || 0}%` }}
                        />
                      </div>
                      <span className="text-sm">{selectedActivity.progress_percent || 0}%</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Control Key</Label>
                    <p className="font-medium">{selectedActivity.control_key}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            {isEditMode ? (
              <>
                <Button variant="outline" onClick={() => setIsEditMode(false)}>Cancel</Button>
                <Button onClick={handleSaveEdit} data-testid="save-activity-btn">Save Changes</Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setShowViewEditDialog(false)}>Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
